import { AitLocation, AitTablePosition } from "./interface";

/**
 * Build a stable base DOM id for a table location.
 * This function composes an id string from the provided `location` and optional `stateId`.
 * The id is useful for generating element ids for table rows, cells and columns.
 *
 * @param location - The table location describing the position (table, column, rowGroup, row, cell)
 * @param stateId - Optional state-specific prefix (defaults to 'table' when not provided)
 * @returns A string id composed from the location and stateId, or `undefined` if the location is
 *          not specific enough (should not happen with valid locations). Examples:
 *          - 'table'
 *          - 'table~column~col-1'
 *          - 'table~header~rowGroup~rg1~row~r1~cell~c1' (example for a header cell)
 */
export const getBaseId = (location: AitLocation, stateId?: string): string | undefined => {
  let id = stateId ?? "table";

  // Table level
  if (location.position === AitTablePosition.table) {
    return id;
  }

  // Column level
  if (location.position === AitTablePosition.column) {
    id += `~column~${location.columnId}`;
    return id;
  }

  // Row group level
  id += `~${location.tableSection}`;
  if (
    location.position === AitTablePosition.header ||
    location.position === AitTablePosition.body
  ) {
    return id;
  }

  // Specific row group
  id += `~rowGroup~${location.rowGroupId}`;
  if (location.position === AitTablePosition.bodyRowGroup) {
    return id;
  }

  // Row level
  id += `~row~${location.rowId}`;
  if (
    location.position === AitTablePosition.headerRow ||
    location.position === AitTablePosition.bodyRow
  ) {
    return id;
  }

  // Cell level
  id += `~cell~${location.cellId}`;
  /* istanbul ignore else */
  if (
    location.position === AitTablePosition.headerCell ||
    location.position === AitTablePosition.bodyCell
  ) {
    return id;
  }
};
